import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../api";

interface Profile {
  name: string;
  headline: string | null;
  location: string | null;
  email: string | null;
  phone: string | null;
  links: Record<string, string>;
  summary: string | null;
  skills: string[];
  roles: Role[];
  projects: Project[];
  education: Education[];
  certifications: string[];
}

interface Role {
  company: string | null;
  title: string | null;
  start: string | null;
  end: string | null;
  location: string | null;
  summary: string | null;
  bullets: string[];
  skills: string[];
}

interface Project {
  name: string;
  summary: string;
  bullets: string[];
  skills: string[];
  url: string | null;
}

interface Education {
  school: string;
  credential: string | null;
  start: string | null;
  end: string | null;
  notes: string | null;
}

interface ProfileMeta {
  extract_status?: "running" | "done" | "failed";
  extract_error?: string;
  extract_started_at?: string;
  extracted_at?: string;
  master_resume_filename?: string;
  [key: string]: unknown;
}

interface ProfileResponse {
  meta: ProfileMeta;
  // Null while the very first extraction is still running.
  profile: Profile | null;
}

export function Profile() {
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState<Profile | null>(null);

  const profileQ = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      try {
        return await api<ProfileResponse>("/profile", token);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
    enabled: !!token,
    // Extraction runs asynchronously; poll while it is in flight.
    refetchInterval: (query) =>
      (query.state.data as ProfileResponse | null)?.meta?.extract_status === "running" ? 2000 : false,
  });

  const extractStatus = profileQ.data?.meta?.extract_status;

  useEffect(() => {
    if (profileQ.data?.profile) setDraft(profileQ.data.profile);
  }, [profileQ.data]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const presign = await api<{ uploadUrl: string; s3Key: string; filename: string }>(
        "/profile/upload-url",
        token,
        {
          method: "POST",
          body: JSON.stringify({ filename: file.name, contentType: file.type }),
        },
      );
      const put = await fetch(presign.uploadUrl, {
        method: "PUT",
        headers: { "content-type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`S3 upload failed: ${put.status}`);
      // Returns 202 as soon as the worker is queued; progress arrives through
      // the profile query's extract_status.
      return api<{ status: string }>("/profile/extract", token, {
        method: "POST",
        body: JSON.stringify({ s3Key: presign.s3Key, filename: presign.filename }),
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile"] }),
  });

  const extracting = upload.isPending || extractStatus === "running";

  const save = useMutation({
    mutationFn: async (p: Profile) =>
      api<{ ok: boolean }>("/profile", token, { method: "PUT", body: JSON.stringify(p) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile"] }),
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-50">Profile</h1>
          <p className="mt-1 text-sm text-slate-400">
            Master profile is the source of truth for tailoring. Upload your resume, then
            review the extracted fields. Tailoring will only ever reorder and emphasize what's
            here — never invent.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInput}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
            }}
          />
          <button
            type="button"
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 transition hover:border-slate-500 hover:bg-slate-800"
            onClick={() => fileInput.current?.click()}
            disabled={extracting}
          >
            {extracting ? "Extracting…" : "Upload master resume"}
          </button>
        </div>
      </header>

      {profileQ.error && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-4 py-2 text-sm text-rose-200">
          Could not load your profile: {(profileQ.error as Error).message}
        </div>
      )}

      {upload.error && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-4 py-2 text-sm text-rose-200">
          {(upload.error as Error).message}
        </div>
      )}

      {!upload.error && extractStatus === "failed" && profileQ.data?.meta?.extract_error && (
        <div className="rounded-md border border-rose-700 bg-rose-950/40 px-4 py-2 text-sm text-rose-200">
          Extraction failed: {profileQ.data.meta.extract_error}
        </div>
      )}

      {extractStatus === "running" && (
        <div className="rounded-md border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm text-slate-300">
          Reading {profileQ.data?.meta?.master_resume_filename ?? "your resume"} and extracting the
          profile. This can take a couple of minutes for a long resume.
        </div>
      )}

      {profileQ.isPending && <p className="text-slate-400">Loading…</p>}

      {!profileQ.isPending && !profileQ.error && !draft && extractStatus !== "running" && (
        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 px-5 py-10 text-center text-sm text-slate-400">
          No profile yet — upload your master resume above to extract one.
        </div>
      )}

      {draft && <Editor draft={draft} setDraft={setDraft} onSave={() => save.mutate(draft)} saving={save.isPending} />}
    </div>
  );
}

function Editor({
  draft,
  setDraft,
  onSave,
  saving,
}: {
  draft: Profile;
  setDraft: (p: Profile) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const update = <K extends keyof Profile>(k: K, v: Profile[K]) => setDraft({ ...draft, [k]: v });

  return (
    <div className="space-y-6">
      <Section title="Basics">
        <Field label="Name" value={draft.name} onChange={(v) => update("name", v)} />
        <Field label="Headline" value={draft.headline ?? ""} onChange={(v) => update("headline", v)} />
        <Field label="Location" value={draft.location ?? ""} onChange={(v) => update("location", v)} />
        <Field label="Email" value={draft.email ?? ""} onChange={(v) => update("email", v)} />
        <Field label="Phone" value={draft.phone ?? ""} onChange={(v) => update("phone", v)} />
        <TextArea
          label="Summary"
          value={draft.summary ?? ""}
          onChange={(v) => update("summary", v)}
          rows={4}
        />
      </Section>

      <Section title={`Skills (${draft.skills.length})`}>
        <ChipList values={draft.skills} onChange={(v) => update("skills", v)} placeholder="Add skill…" />
      </Section>

      <Section title={`Roles (${draft.roles.length})`}>
        <p className="text-xs text-slate-500">Each role is the source for tailored bullets. JSON-edit for now; rich editor lands later.</p>
        <RawJson
          value={draft.roles}
          onChange={(v) => update("roles", v as Role[])}
        />
      </Section>

      <Section title={`Projects (${draft.projects.length})`}>
        <RawJson value={draft.projects} onChange={(v) => update("projects", v as Project[])} />
      </Section>

      <Section title={`Education (${draft.education.length})`}>
        <RawJson value={draft.education} onChange={(v) => update("education", v as Education[])} />
      </Section>

      <div className="flex justify-end">
        <button
          type="button"
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition hover:opacity-90 disabled:opacity-50"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save profile"}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-800 bg-slate-900 p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input
        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function TextArea({
  label,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <textarea
        rows={rows}
        className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function ChipList({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2.5 py-1 text-xs text-slate-200"
          >
            {v}
            <button
              type="button"
              className="text-slate-500 transition hover:text-rose-300"
              onClick={() => onChange(values.filter((_, idx) => idx !== i))}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100 outline-none focus:border-slate-500"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              onChange([...values, draft.trim()]);
              setDraft("");
              e.preventDefault();
            }
          }}
        />
      </div>
    </div>
  );
}

function RawJson<T>({ value, onChange }: { value: T; onChange: (v: T) => void }) {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setText(JSON.stringify(value, null, 2));
  }, [value]);

  return (
    <div>
      <textarea
        rows={Math.min(20, text.split("\n").length + 1)}
        className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 font-mono text-xs text-slate-100 outline-none focus:border-slate-500"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          try {
            onChange(JSON.parse(text) as T);
            setErr(null);
          } catch (e) {
            setErr((e as Error).message);
          }
        }}
      />
      {err && <p className="mt-1 text-xs text-rose-300">{err}</p>}
    </div>
  );
}
